import React, { useState, useMemo } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  DataPagination,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Skeleton,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@musa.tech/design-system";
import {
  ChevronUp,
  ChevronDown,
  Download,
  Eye,
  FileText,
  GripVertical,
  Settings,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { format, parse } from "date-fns";
import { removeGeneratorPrefix } from "@/utils/establishmentUtils";
import { getMaterialDisplayLabel } from "@/utils/materialSystem";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useDemoMode } from "@/contexts/DemoModeContext";
import { usePermissions } from "@/hooks/useUnifiedAuth";
import { PERMISSIONS } from "@/constants/permissions";
import { maskEstablishmentName } from "@/utils/demoMode";
import ModalCollectEvidence from "./ModalCollectEvidence";
import ModalCollectMtrFile from "./ModalCollectMtrFile";
import ModalCollectCdfFile from "./ModalCollectCdfFile";
import { useGatewayMaterials } from "@/hooks/useGatewayMaterials";
import { buildMaterialUnitMap, formatQuantity, unitForMaterial } from "@/utils/materialUnit";

interface Collect {
  collect_id: string;
  establishment_id: string;
  establishment_name: string | null;
  planned_date: string;
  kind_of_material: string | null;
  material_id?: number | null;
  packaging_name: string | null;
  units: number | null;
  weight: number | null;
  hauler_name: string | null;
  sla_status: string | null;
  has_collect_evidence: boolean | null;
  has_mtr: boolean | null;
  has_cdf: boolean | null;
  mtr_link: string | null;
  establishments?: { establishment_name: string };
}

interface CollectsTableProps {
  data: Collect[];
  isLoading: boolean;
  onExportCSV: () => void;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Ação extra no header (ex.: botão/modal de download em massa de MTRs). */
  bulkDownloadSlot?: React.ReactNode;
}

type SortDirection = "asc" | "desc" | null;
type SortableColumn =
  | "collect_id"
  | "establishment"
  | "units"
  | "weight"
  | "acondicionamento"
  | "material"
  | "has_collect_evidence"
  | "has_mtr"
  | "has_cdf"
  | "date"
  | "status";

interface ColumnConfig {
  key: SortableColumn;
  label: string;
  visible: boolean;
  sortable: boolean;
  alignment: "left" | "center" | "right";
}

type BadgeVariant = "default" | "secondary" | "destructive" | "success" | "warning" | "info" | "outline" | "primary";

const STATUS_CONFIG: Record<string, { label: string; variant: BadgeVariant }> = {
  collected:             { label: "Coletado",            variant: "success" },
  destined:              { label: "Destinado",           variant: "info" },
  fault:                 { label: "Falha",               variant: "destructive" },
  realizado_no_prazo:    { label: "Realizado No Prazo",  variant: "success" },
  realizado_com_atraso:  { label: "Realizado Com Atraso",variant: "warning" },
  nao_realizado:         { label: "Não Realizado",       variant: "destructive" },
  nao_realizada:         { label: "Não Realizado",       variant: "destructive" },
  planned:               { label: "Planejado",           variant: "secondary" },
  scheduled:             { label: "Agendado",            variant: "secondary" },
  pending:               { label: "Pendente",            variant: "secondary" },
  in_progress:           { label: "Em Andamento",        variant: "warning" },
  "in progress":         { label: "Em Andamento",        variant: "warning" },
  completed:             { label: "Concluído",           variant: "success" },
  cancelled:             { label: "Cancelado",           variant: "secondary" },
  canceled:              { label: "Cancelado",           variant: "secondary" },
};

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <Badge variant="secondary">N/A</Badge>;
  const key = status.toLowerCase();
  const config = STATUS_CONFIG[key];
  if (config) return <Badge variant={config.variant}>{config.label}</Badge>;
  const label = status.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
  return <Badge variant="secondary">{label}</Badge>;
}

function SortableColumnItem({
  column,
  onToggle,
}: {
  column: ColumnConfig;
  onToggle: (key: SortableColumn) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: column.key });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-neutral-100 rounded-sm cursor-default select-none"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-neutral-400 hover:text-neutral-700 touch-none"
        tabIndex={-1}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Checkbox
        id={`col-${column.key}`}
        checked={column.visible}
        onCheckedChange={() => onToggle(column.key)}
      />
      <label htmlFor={`col-${column.key}`} className="cursor-pointer flex-1 text-neutral-800">
        {column.label}
      </label>
    </div>
  );
}

export const CollectsTable = ({
  data,
  isLoading,
  onExportCSV,
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  bulkDownloadSlot,
}: CollectsTableProps) => {
  const { hasAnyPermission, hasBaselinePermission } = usePermissions();
  const { isDemoMode } = useDemoMode();
  const { trackButtonClick } = useAnalytics();
  // Unidade da quantidade de cada coleta (kg, Lt ou un) vem do catálogo de materiais; sem catálogo,
  // tudo cai em kg — a tabela não espera por ele.
  const { data: materials } = useGatewayMaterials();
  const materialUnits = useMemo(() => buildMaterialUnitMap(materials), [materials]);
  const [modalCollectId, setModalCollectId] = useState<string | null>(null);
  const [modalType, setModalType] = useState<"evidence" | "mtr" | "cdf" | null>(null);

  // Evidências de coleta — perfil "Visualização de evidências" (GR-637).
  //
  // Aceita as DUAS roles de propósito: a nova, concedida pelo grupo
  // `enterprise`, e a legada `view-collect-evidence`, que existe como grant
  // direto na conta de quem já tinha o acesso antes do catálogo (caso Daki,
  // GR-563). Enquanto o script de migração não converter esses grants, tirar a
  // legada aqui tiraria acesso de cliente em produção.
  //
  // NÃO há o escape de "não migrado" que as travas de rota têm (GR-561, regra
  // 16): aquela regra protege quem PERDERIA acesso na virada. Evidência é
  // acesso aditivo — quem não tem, nunca teve. Liberar para todo não migrado
  // aqui não seria proteção, seria alargar acesso.
  const canViewEvidence = hasAnyPermission(
    PERMISSIONS.VIEW_COLLECTS_EVIDENCE,
    PERMISSIONS.LEGACY_VIEW_COLLECT_EVIDENCE,
  );

  const [sortColumn, setSortColumn] = useState<SortableColumn>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: "collect_id",          label: "ID da Coleta",       visible: false, sortable: false, alignment: "left" },
    { key: "establishment",       label: "Estabelecimento",    visible: true,  sortable: true,  alignment: "left" },
    { key: "units",               label: "Unidades",           visible: true,  sortable: true,  alignment: "center" },
    { key: "weight",              label: "Quantidade coletada", visible: true, sortable: true,  alignment: "center" },
    { key: "acondicionamento",    label: "Acondicionamento",   visible: true,  sortable: false, alignment: "center" },
    { key: "material",            label: "Resíduo",            visible: true,  sortable: true,  alignment: "center" },
    { key: "date",                label: "Data",               visible: true,  sortable: true,  alignment: "center" },
    { key: "status",              label: "Status de Coleta",   visible: true,  sortable: true,  alignment: "center" },
    { key: "has_mtr",             label: "MTR",                visible: true,  sortable: false, alignment: "center" },
    { key: "has_cdf",             label: "CDF",                visible: true,  sortable: false, alignment: "center" },
    { key: "has_collect_evidence",label: "Evidências",         visible: true,  sortable: false, alignment: "center" },
  ]);

  const sensors = useSensors(useSensor(PointerSensor));

  const sortedData = useMemo(() => {
    if (!sortColumn || !sortDirection) return data;
    return [...data].sort((a, b) => {
      let aVal: any;
      let bVal: any;
      switch (sortColumn) {
        case "establishment":
          aVal = a.establishments?.establishment_name || a.establishment_name || "";
          bVal = b.establishments?.establishment_name || b.establishment_name || "";
          break;
        case "units":  aVal = a.units  ?? 0; bVal = b.units  ?? 0; break;
        case "weight": aVal = a.weight ?? 0; bVal = b.weight ?? 0; break;
        case "material": aVal = a.kind_of_material || ""; bVal = b.kind_of_material || ""; break;
        case "date":   aVal = new Date(a.planned_date); bVal = new Date(b.planned_date); break;
        case "status": aVal = a.sla_status || ""; bVal = b.sla_status || ""; break;
        default: return 0;
      }
      if (typeof aVal === "string") { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortColumn, sortDirection]);

  const handleSort = (column: SortableColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const toggleColumnVisibility = (key: SortableColumn) => {
    setColumns((prev) => prev.map((c) => c.key === key ? { ...c, visible: !c.visible } : c));
  };

  const reorderColumns = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setColumns((prev) => {
        const oldIndex = prev.findIndex((c) => c.key === active.id);
        const newIndex = prev.findIndex((c) => c.key === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  // A permissão filtra a coluna FORA da lista, não a marca como oculta.
  //
  // Duas razões, e as duas eram bug antes: (1) `columns` é `useState`, então
  // uma flag calculada do perfil — que chega depois, por query — congelava no
  // primeiro render e nunca voltava; (2) o menu "Colunas" itera `columns`
  // inteiro, então uma coluna apenas `visible: false` continuava lá para
  // qualquer um marcar. Fora da lista, ela some dos dois lugares e acompanha
  // o perfil quando ele resolve.
  const allowedColumns = useMemo(
    () =>
      columns.filter(
        (c) => c.key !== "has_collect_evidence" || canViewEvidence,
      ),
    [columns, canViewEvidence],
  );

  const visibleColumns = allowedColumns.filter((c) => c.visible);

  const startItem = totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem   = totalItems > 0 ? Math.min((currentPage - 1) * pageSize + sortedData.length, totalItems) : 0;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Detalhes das coletas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-10 w-full" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Detalhes das coletas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <Text variant="body" className="text-neutral-500">Nenhuma coleta encontrada</Text>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-white border-neutral-200">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-4 md:px-6">
          <CardTitle>Detalhes das coletas</CardTitle>
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
            <Text variant="body" size="small" className="text-neutral-500 text-center sm:text-left">
              Mostrando {startItem}–{endItem} de {totalItems} registros
            </Text>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings className="h-4 w-4 mr-2" />
                  Colunas
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-white p-1">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={reorderColumns}>
                  <SortableContext items={allowedColumns.map((c) => c.key)} strategy={verticalListSortingStrategy}>
                    {allowedColumns.map((column) => (
                      <SortableColumnItem key={column.key} column={column} onToggle={toggleColumnVisibility} />
                    ))}
                  </SortableContext>
                </DndContext>
              </DropdownMenuContent>
            </DropdownMenu>

            {hasBaselinePermission(PERMISSIONS.DOWNLOAD_COLLECTS_HISTORY_CSV) && (
            <Button
              variant="outline"
              size="sm"
              data-testid="historico-exportar-csv"
              onClick={() => {
                trackButtonClick({ buttonLabel: "historico_coletas_exportar_csv" });
                onExportCSV();
              }}
              disabled={!data || data.length === 0}
            >
              <Download className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="truncate">Exportar CSV</span>
            </Button>
            )}

            {bulkDownloadSlot}
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <div className="bg-white rounded-none border-0 overflow-hidden">
              <DataTable>
                <DataTableHeader>
                  <DataTableRow>
                    {visibleColumns.map((col) => (
                      <DataTableHead
                        key={col.key}
                        className={`px-4 py-3 ${col.key === "establishment" ? "min-w-[200px]" : ""} ${col.sortable ? "cursor-pointer select-none" : ""}`}
                        onClick={col.sortable ? () => handleSort(col.key) : undefined}
                      >
                        <div className={`flex items-center gap-1 whitespace-nowrap ${col.alignment === "center" ? "justify-center" : col.alignment === "right" ? "justify-end" : "justify-start"}`}>
                          <span className={sortColumn === col.key ? "font-bold text-neutral-900" : ""}>
                            {col.label}
                          </span>
                          {col.sortable && sortColumn === col.key && (
                            sortDirection === "asc"
                              ? <ChevronUp className="h-3.5 w-3.5" />
                              : <ChevronDown className="h-3.5 w-3.5" />
                          )}
                        </div>
                      </DataTableHead>
                    ))}
                  </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                  {sortedData.map((collect, index) => {
                    const rawName = collect.establishments?.establishment_name || collect.establishment_name;
                    const establishmentName = maskEstablishmentName(
                      removeGeneratorPrefix(rawName, isDemoMode),
                      isDemoMode,
                    );

                    return (
                      <DataTableRow key={`${collect.collect_id}-${index}`}>
                        {visibleColumns.map((col) => (
                          <DataTableCell
                            key={col.key}
                            className={`px-4 py-3 ${col.alignment === "center" ? "text-center" : col.alignment === "right" ? "text-right" : "text-left"}`}
                          >
                            {col.key === "collect_id" && (
                              <span className="text-xs text-neutral-500 font-mono">{collect.collect_id}</span>
                            )}
                            {col.key === "establishment" && (
                              <span className="block max-w-[200px] truncate" title={establishmentName}>
                                {establishmentName}
                              </span>
                            )}
                            {col.key === "units" && <span>{collect.units ?? 0}</span>}
                            {col.key === "weight" && (
                              <span className="whitespace-nowrap">
                                {(collect.sla_status !== "planned" &&
                                  formatQuantity(collect.weight, unitForMaterial(collect.material_id, materialUnits))) ||
                                  "—"}
                              </span>
                            )}
                            {col.key === "acondicionamento" && (
                              <span
                                className="inline-block max-w-[180px] truncate align-middle"
                                title={collect.packaging_name ?? "—"}
                              >
                                {collect.packaging_name ?? "—"}
                              </span>
                            )}
                            {col.key === "material" && (
                              <span
                                className="inline-block max-w-[180px] truncate align-middle"
                                title={getMaterialDisplayLabel(collect.kind_of_material || "")}
                              >
                                {getMaterialDisplayLabel(collect.kind_of_material || "")}
                              </span>
                            )}
                            {col.key === "date" && (
                              <span>
                                {format(parse(collect.planned_date, "yyyy-MM-dd", new Date()), "dd/MM/yy")}
                              </span>
                            )}
                            {col.key === "status" && (
                              <div className="flex items-center justify-center">
                                <StatusBadge status={collect.sla_status} />
                              </div>
                            )}
                            {col.key === "has_collect_evidence" && (
                              !isDemoMode && collect.has_collect_evidence ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        data-testid="historico-visualizar-evidencias"
                                        onClick={() => {
                                          trackButtonClick({
                                            buttonLabel: "historico_coletas_visualizar_evidencias",
                                            collection_id: collect.collect_id,
                                          });
                                          setModalCollectId(collect.collect_id);
                                          setModalType("evidence");
                                        }}
                                      >
                                        <Eye className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Ver evidências</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : <span className="text-neutral-400">—</span>
                            )}
                            {col.key === "has_mtr" && (
                              !isDemoMode && collect.has_mtr ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        data-testid="historico-visualizar-mtr"
                                        onClick={() => {
                                          trackButtonClick({
                                            buttonLabel: "historico_coletas_visualizar_mtr",
                                            collection_id: collect.collect_id,
                                          });
                                          setModalCollectId(collect.collect_id);
                                          setModalType("mtr");
                                        }}
                                      >
                                        <FileText className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Ver MTR</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : <span className="text-neutral-400">—</span>
                            )}
                            {col.key === "has_cdf" && (
                              !isDemoMode && collect.has_cdf ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        data-testid="historico-visualizar-cdf"
                                        onClick={() => {
                                          trackButtonClick({
                                            buttonLabel: "historico_coletas_visualizar_cdf",
                                            collection_id: collect.collect_id,
                                          });
                                          setModalCollectId(collect.collect_id);
                                          setModalType("cdf");
                                        }}
                                      >
                                        <FileText className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Ver CDF</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : <span className="text-neutral-400">—</span>
                            )}
                          </DataTableCell>
                        ))}
                      </DataTableRow>
                    );
                  })}
                </DataTableBody>
              </DataTable>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center py-4 px-4 md:px-6">
              <DataPagination
                page={currentPage}
                pageSize={pageSize}
                total={totalItems}
                onChange={onPageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {modalType === "evidence" && (
        <ModalCollectEvidence
          collectId={modalCollectId}
          onOpenChange={(open) => { if (!open) { setModalCollectId(null); setModalType(null); } }}
        />
      )}
      {modalType === "mtr" && (
        <ModalCollectMtrFile
          collectId={modalCollectId}
          onOpenChange={(open) => { if (!open) { setModalCollectId(null); setModalType(null); } }}
        />
      )}
      {modalType === "cdf" && (
        <ModalCollectCdfFile
          collectId={modalCollectId}
          onOpenChange={(open) => { if (!open) { setModalCollectId(null); setModalType(null); } }}
        />
      )}
    </>
  );
};
